import {
  useMarkListed,
  useUnlistInventory,
  useMarkCannotList,
  useReEnableInventory,
  useMoveToMarketplace,
  useSetBuybackFveIntent,
  useSetBuybackAccount,
  useCompleteBuyback,
  useDeleteInventorySaleMatch,
  type BuybackAccountInput,
} from "@workspace/api-client-react";

export type BuybackFveIntentValue = "buy_back" | "sell" | "cannot_list" | "surrender" | null;

// Per-call callbacks forwarded to react-query's `mutate`. These run only while
// the firing component is still mounted, so a row whose popover has closed (or
// unmounted during a background refresh) can't have its state poked after the
// fact — which is exactly what we want for closing the popover on completion.
export interface MutateOpts {
  onSuccess?: () => void;
  onError?: (err: unknown) => void;
  onSettled?: () => void;
}

export interface FveTransitions {
  markListed: (
    id: number,
    listedPrice?: number | null,
    opts?: MutateOpts,
  ) => void;
  unlist: (id: number, opts?: MutateOpts) => void;
  cannotList: (id: number, reason: string, opts?: MutateOpts) => void;
  reEnable: (id: number, opts?: MutateOpts) => void;
  moveToMarketplace: (
    id: number,
    target: "reach" | "skybox",
    force?: boolean,
    opts?: MutateOpts,
  ) => void;
  setBuybackFveIntent: (
    id: number,
    intent: BuybackFveIntentValue,
    opts?: MutateOpts,
  ) => void;
  setBuybackAccount: (
    id: number,
    data: BuybackAccountInput,
    opts?: MutateOpts,
  ) => void;
  completeBuyback: (id: number, opts?: MutateOpts) => void;
  unmatchSale: (id: number, opts?: MutateOpts) => void;
  busyId: number | null;
}

/**
 * Wire the operator FVE-status transition endpoints to a single shared helper.
 * The parent supplies `invalidate` so each table can refetch the right query
 * after a mutation lands, mirroring the existing manual-fields/hold hooks.
 */
export function useFveTransitions(invalidate: () => void): FveTransitions {
  const markListedM = useMarkListed({ mutation: { onSuccess: invalidate } });
  const unlistM = useUnlistInventory({ mutation: { onSuccess: invalidate } });
  const cannotListM = useMarkCannotList({ mutation: { onSuccess: invalidate } });
  const reEnableM = useReEnableInventory({ mutation: { onSuccess: invalidate } });
  const moveM = useMoveToMarketplace({ mutation: { onSuccess: invalidate } });
  const setIntentM = useSetBuybackFveIntent({ mutation: { onSuccess: invalidate } });
  const setAccountM = useSetBuybackAccount({ mutation: { onSuccess: invalidate } });
  const completeM = useCompleteBuyback({ mutation: { onSuccess: invalidate } });
  const unmatchSaleM = useDeleteInventorySaleMatch({ mutation: { onSuccess: invalidate } });

  const busyId = markListedM.isPending
    ? (markListedM.variables?.id ?? null)
    : unlistM.isPending
      ? (unlistM.variables?.id ?? null)
      : cannotListM.isPending
        ? (cannotListM.variables?.id ?? null)
        : reEnableM.isPending
          ? (reEnableM.variables?.id ?? null)
          : moveM.isPending
            ? (moveM.variables?.id ?? null)
            : setIntentM.isPending
              ? (setIntentM.variables?.id ?? null)
              : setAccountM.isPending
                ? (setAccountM.variables?.id ?? null)
                : completeM.isPending
                  ? (completeM.variables?.id ?? null)
                  : unmatchSaleM.isPending
                    ? (unmatchSaleM.variables?.id ?? null)
                    : null;

  return {
    markListed: (id, listedPrice, opts) =>
      markListedM.mutate(
        { id, data: { listedPrice: listedPrice ?? null } },
        opts,
      ),
    unlist: (id, opts) => unlistM.mutate({ id }, opts),
    cannotList: (id, reason, opts) =>
      cannotListM.mutate({ id, data: { reason } }, opts),
    reEnable: (id, opts) => reEnableM.mutate({ id }, opts),
    moveToMarketplace: (id, target, force, opts) =>
      moveM.mutate(
        { id, data: force ? { target, force: true } : { target } },
        opts,
      ),
    setBuybackFveIntent: (id, intent, opts) =>
      setIntentM.mutate({ id, data: { intent } }, opts),
    setBuybackAccount: (id, data, opts) =>
      setAccountM.mutate({ id, data }, opts),
    completeBuyback: (id, opts) => completeM.mutate({ id }, opts),
    unmatchSale: (id, opts) => unmatchSaleM.mutate({ id }, opts),
    busyId,
  };
}
